import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Layout from '../components/Layout';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'react-hot-toast';
import { Plus, Search, Eye, RefreshCw } from 'lucide-react';
import axios from 'axios';
import DataTable from '../components/common/DataTable';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const getStatusColor = (status) => {
  switch (status) {
    case 'CREATED': return 'bg-orange-100 text-orange-800';
    case 'PICKED': return 'bg-yellow-100 text-yellow-800';
    case 'PACKED': return 'bg-orange-100 text-orange-800';
    case 'GOODS_ISSUED': return 'bg-green-100 text-green-800';
    case 'CANCELLED': return 'bg-red-100 text-red-800';
    default: return 'bg-gray-100 text-gray-800';
  }
};

const DeliveryList = () => {
  const navigate = useNavigate();
  const { canWrite } = useAuth();
  const [deliveries, setDeliveries] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [filters, setFilters] = useState({
    status: '',
    sales_order_number: '',
    ship_to_party: ''
  });
  const [customers, setCustomers] = useState([]);
  const [salesOrders, setSalesOrders] = useState([]);

  useEffect(() => {
    loadMasterData();
    loadDeliveries();
  // eslint-disable-next-line
  }, []);

  const loadMasterData = async () => {
    try {
      const [custRes, soRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/customers`).catch(() => ({ data: [] })),
        axios.get(`${BACKEND_URL}/api/otc/sales-orders`).catch(() => ({ data: [] }))
      ]);
      setCustomers(custRes.data || []);
      setSalesOrders(soRes.data || []);
    } catch (error) {
      console.error('Error loading master data:', error);
    }
  };

  const loadDeliveries = async () => {
    setIsLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.status) params.append('status', filters.status);
      if (filters.sales_order_number) params.append('sales_order_number', filters.sales_order_number);
      if (filters.ship_to_party) params.append('ship_to_party', filters.ship_to_party);

      const response = await axios.get(`${BACKEND_URL}/api/otc/deliveries?${params.toString()}`);
      setDeliveries(response.data || []);
    } catch (error) {
      console.error('Error loading deliveries:', error);
      const errorMsg = error.response?.data?.detail || error.message || 'Unknown error';
      toast.error(`Failed to load deliveries: ${errorMsg}`);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSearch = () => loadDeliveries();
  const handleClearFilters = () => setFilters({ status: '', sales_order_number: '', ship_to_party: '' });

  const getCustomerName = (customerId) => {
    const customer = customers.find(c => c.customer_id === customerId);
    return customer?.customer_name || customer?.name || customerId;
  };

  const columns = [
    { key: 'delivery_number', header: 'Delivery #', filterable: true, accessor: (d) => d.delivery_number,
      render: (d) => <span className="font-mono font-bold text-orange-600">{d.delivery_number}</span> },
    { key: 'sales_order_number', header: 'Sales Order', filterable: true, accessor: (d) => d.sales_order_number,
      render: (d) => <span className="font-mono">{d.sales_order_number}</span> },
    { key: 'ship_to_party', header: 'Ship-to Party', filterable: true,
      accessor: (d) => `${getCustomerName(d.ship_to_party)} ${d.ship_to_party}`,
      render: (d) => (
        <div>
          <p className="font-medium">{getCustomerName(d.ship_to_party)}</p>
          <p className="text-xs text-gray-500">{d.ship_to_party}</p>
        </div>
      ) },
    { key: 'planned_gi', header: 'Planned GI Date', accessor: (d) => d.planned_goods_issue_date?.split('T')[0] },
    { key: 'actual_gi', header: 'Actual GI Date', accessor: (d) => d.actual_goods_issue_date?.split('T')[0] || '-' },
    { key: 'items', header: 'Items', align: 'center', accessor: (d) => d.items?.length || 0 },
    { key: 'status', header: 'Status',
      render: (d) => (
        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(d.status)}`}>
          {d.status?.replace('_', ' ')}
        </span>
      ) },
    { key: 'actions', header: 'Actions', align: 'right',
      render: (d) => (
        <div className="flex justify-end gap-1">
          <Button size="sm" variant="outline" onClick={() => navigate(`/otc/deliveries/${d.delivery_number}`)} title="View Delivery">
            <Eye className="w-4 h-4" />
          </Button>
        </div>
      ) },
  ];

  return (
    <Layout>
      <div className="p-6">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Deliveries</h1>
            <p className="text-sm text-gray-600 mt-1">Manage outbound deliveries and goods issue</p>
          </div>
          {canWrite('delivery') && (
          <Button onClick={() => navigate('/otc/deliveries/create')} data-testid="create-delivery-btn">
            <Plus className="w-4 h-4 mr-2" />
            Create Delivery
          </Button>
          )}
        </div>

        {/* Filters */}
        <Card className="p-4 mb-6">
          <div className="flex flex-wrap items-end gap-4">
            <div className="w-48">
              <label className="text-sm font-medium text-gray-700 mb-1 block">Status</label>
              <Select value={filters.status || '__ALL__'} onValueChange={(v) => setFilters({...filters, status: v === '__ALL__' ? '' : v})}>
                <SelectTrigger><SelectValue placeholder="All Statuses" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__ALL__">All Statuses</SelectItem>
                  <SelectItem value="CREATED">Created</SelectItem>
                  <SelectItem value="PICKED">Picked</SelectItem>
                  <SelectItem value="PACKED">Packed</SelectItem>
                  <SelectItem value="GOODS_ISSUED">Goods Issued</SelectItem>
                  <SelectItem value="CANCELLED">Cancelled</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="w-48">
              <label className="text-sm font-medium text-gray-700 mb-1 block">Sales Order</label>
              <Select value={filters.sales_order_number || '__ALL__'} onValueChange={(v) => setFilters({...filters, sales_order_number: v === '__ALL__' ? '' : v})}>
                <SelectTrigger><SelectValue placeholder="All Orders" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__ALL__">All Sales Orders</SelectItem>
                  {salesOrders.map(so => (
                    <SelectItem key={so.sales_order_number} value={so.sales_order_number}>
                      {so.sales_order_number}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="w-48">
              <label className="text-sm font-medium text-gray-700 mb-1 block">Ship-to Party</label>
              <Select value={filters.ship_to_party || '__ALL__'} onValueChange={(v) => setFilters({...filters, ship_to_party: v === '__ALL__' ? '' : v})}>
                <SelectTrigger><SelectValue placeholder="All Customers" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__ALL__">All Customers</SelectItem>
                  {customers.map(c => (
                    <SelectItem key={c.customer_id} value={c.customer_id}>
                      {c.customer_id} - {c.customer_name || c.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Button onClick={handleSearch} data-testid="search-btn">
              <Search className="w-4 h-4 mr-2" />
              Search
            </Button>
            <Button variant="outline" onClick={handleClearFilters}>
              <RefreshCw className="w-4 h-4 mr-2" />
              Clear
            </Button>
          </div>
        </Card>

        {/* Deliveries Table */}
        <Card className="p-6">
          <DataTable
            testid="deliveries-table"
            columns={columns}
            data={deliveries}
            loading={isLoading}
            rowKey={(d) => d.delivery_number}
            emptyText="No deliveries found."
            emptyHint="Create a delivery from a sales order to get started."
            rowProps={(d) => ({ 'data-testid': `delivery-${d.delivery_number}` })}
          />
        </Card>
      </div>
    </Layout>
  );
};

export default DeliveryList;
